import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Config } from '../../config/config';
import { METHOD_TYPE } from '../../constants/service.constants';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { ServiceRouter } from '../../services/core/http-service-router.service';
import { Environment } from '../../config/environment';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { Subscription } from 'rxjs';
import { AppointmentsService } from 'src/app/services/scheduling/appointment/appointments.service';
import { PROCEDURE_CONSTANTS, APPOINTMENT_STATUS } from 'src/app/constants/scheduling.constants';
import { EheAnalyticsDirective } from '../shared/ehe-analytics/ehe-analytics.directive';

/**
 * Referral component
 */
@Component({
	selector: 'app-referral',
	templateUrl: './referral.component.html',
	styleUrls: ['./referral.component.scss'],
	providers: [EheAnalyticsDirective],
})
export class ReferralComponent implements OnInit, OnDestroy {
	currentReferrals: string;
	pastReferrals: string;
	/** Side nav selected index */
	selectedIndex = 0;
	/** Referral eligibility flag */
	hasEligibility: boolean = false;
	/** Referral eligibility message */
	eligibilityMessage = '';
	/** holds data carrier subscriptions */
	subscription: Subscription;
	/** Referral side nav items */
	navItems = [
		{
			title: 'Current Referrals',
			expanded: false,
			route: '/wellness/referral/current/summary',
			eligibilityRoute: '/wellness/referral/eligibility',
			subNavItems: [
				{
					title: 'Summary',
					route: '/wellness/referral/current/summary',
					active: false,
				},
				{
					title: 'Referral Requests',
					route: '/wellness/referral/current/requests',
					active: false,
				},
				{
					title: 'Referral Ready',
					route: '/wellness/referral/current/ready',
					active: false,
				},
				{
					title: 'Appointment Requests',
					route: '/wellness/referral/current/pending/appointment/request',
					active: false,
				},
				{
					title: 'Confirmed Appointments',
					route: '/wellness/referral/current/confirmed/appointments',
					active: false,
				},
			],
		},
		{
			title: 'Request Referral',
			route: '/wellness/referral/request',
			eligibilityRoute: '/wellness/referral/eligibility',
		},
		{
			title: 'Referral History',
			expanded: false,
			route: '/wellness/referral/history/summary',
			eligibilityRoute: '/wellness/referral/eligibility',
			subNavItems: [
				{
					title: 'Summary',
					route: '/wellness/referral/history/summary',
					active: false,
				},
				{
					title: 'Referral Requests',
					route: '/wellness/referral/history/requests',
					active: false,
				},
				{
					title: 'Referral Ready',
					route: '/wellness/referral/history/ready',
					active: false,
				},
				{
					title: 'Appointment Requests',
					route: '/wellness/referral/history/pending/appointment/request',
					active: false,
				},
				{
					title: 'Confirmed Appointments',
					route: '/wellness/referral/history/confirmed/appointments',
					active: false,
				},
			],
		},
	];

	/** Referral side nav menu for mobile view */
	sidebarMenu = [
		{
			menuName: 'Current Referrals',
			routerLink: '/wellness/referral/current/summary',
		},
		{ menuName: 'Request Referral', routerLink: '/wellness/referral/request' },
		{
			menuName: 'Referral History',
			routerLink: '/wellness/referral/history/summary',
		},
	];
	/** analytics data bean */
	analyticsData: AnalyticsBean;
	/** Procedure constants (Physical Exam) */
	procedureConstants = PROCEDURE_CONSTANTS;
	/** Appointment status constants */
	apptStatus = APPOINTMENT_STATUS;
	/**
	 * @param serviceRouter service router
	 * @param config config file that holds the service end points
	 * @param spinner spinner component
	 * @param environment - environment file that supplies env values
	 * @param router - angular router
	 * @param appointmentService - appointment service
	 */
	constructor(
		private serviceRouter: ServiceRouter,
		private config: Config,
		private spinner: SpinnerComponent,
		private environment: Environment,
		public router: Router,
		private appointmentService: AppointmentsService
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'referral';
		this.analyticsData.pageUrl = '/wellness/referral/current/summary';
	}

	/**
	 * Component initialization - fetches referral information
	 */
	ngOnInit() {
		this.getReferrals();
	}

	/**
	 * Retrieving list of referrals if the patient has eligibility
	 * Else displaying an eligibility message based on upcoming physical exam status
	 */
	getReferrals() {
		this.spinner.startLoader();
		this.subscription = this.serviceRouter
			.makeRequest(
				this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
				this.config.url.referralApi.getCurrentReferrals,
				METHOD_TYPE.SECURED_GET,
				''
			)
			.subscribe(
				(data: any) => {
					this.spinner.stopLoader();
					if (data.patientEligiblity) {
						this.hasEligibility = true;
					}
				},
				err => {
					// Error
					this.spinner.stopLoader();
				}
			);
	}

	/** Making side nav item active and expanded on click */
	makeNavItemActive(index) {
		this.resetAllItems();
		this.navItems[index].expanded = true;
		this.selectedIndex = index;
	}

	/** Making side subnav item active and expanded on click */
	makeSubNavItemActive(navIndex, subNavIndex) {
		this.resetAllItems();
		this.navItems[navIndex].expanded = true;
		this.navItems[navIndex].subNavItems[subNavIndex].active = true;
	}

	/**
	 * Resetting previously active/expanded nav item before making any nav/subnav item active/expanded
	 */
	resetAllItems() {
		this.navItems.forEach(navItem => {
			navItem.expanded = false;
			if (navItem.subNavItems) {
				navItem.subNavItems.forEach(subNavItem => {
					subNavItem.active = false;
				});
			}
		});
	}

	/**
	 * Sends an email message to referral team
	 */
	sendMessage() {
		this.analyticsData.id = 'Send Message to referral team';
		this.analyticsData.redirectedTo = '/messaging/compose';
		this.analyticsData.placement = 'Referral contact section';
		this.router.navigate(['/messaging/compose'], {
			queryParams: { fromReferral: true },
		});
	}

	/**
	 * @param index of the selected item
	 * getting currently selected index
	 */
	getSelectedIndex(index) {
		this.selectedIndex = index;
	}
	/**
	 * Destroys component
	 */
	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}
